import { useTokenStore } from '@/stores/token';
import { AutAccount, CountryUtil, LanguageUtil, Message, SelectOption, UserTypes } from '@fibl/oxs-common';
import { post } from '@/util/request';
import { EAV, SearchRepo } from '@fibl/zdb-common';
import { toLocale } from '@/util/locale-helper';
import { useRelatedStore } from '@/stores/related';
import { useWebsiteStore } from '@/stores/website';
import { getI18n } from '@/util/i18n';

export async function addToConversation(id: string, message: Message.Message) {
	const config: Message.AddToConversation = {
		conversation: id,
		message: message,
	};
	await post('oxs/message/addtoconversation', config);
}

export async function sendAutToSupOfferRejection(
	reason: string,
	attachments: Message.MessageAttachment[],
	offerId: string,
) {
	const message: Message.Message = {
		sent: new Date().toJSON(),
		content: reason,
		party: 0,
		attachments: attachments,
	};
	await autToSupOfferConversation(message, 'autToSupOfferInCountry', offerId);
}

export async function autToSupOfferConversation(
	message: Message.Message,
	contextId: Message.MessageContextKeys,
	offer: string,
) {
	const tokenStore = useTokenStore();
	const i18n = getI18n();
	if (contextId !== 'autToSupOfferInCountry') throw new Error(i18n.t('repository.contextOfferError'));
	if (!offer) throw new Error(i18n.t('repository.offerIdMissing'));
	const accData = tokenStore.main.details.staticData as AutAccount['staticData'];
	const country = accData.country;
	const config: Message.AutToSupOffer = {
		message: message,
		context: {
			country: country,
			offer: offer
		},
	};
	await post('oxs/message/createauttosupofferconversation', config);
}

export async function autToSupReleaseConversation(
	message: Message.Message,
	contextId: Message.MessageContextKeys,
	sup: string,
	country: CountryUtil.CountryCode
) {
	const i18n = getI18n();
	if (contextId !== 'supAcceptanceInCountry') throw new Error(i18n.t('repository.contextSupError'));
	if (!sup) throw new Error(i18n.t('repository.supIdMissing'));
	const config: Message.SupAcceptanceCountry = {
		message: message,
		context: {
			country: country,
			sup: sup
		},
	};
	await post('oxs/message/createcountrysupreleaseconversation', config);
}

export async function agrToSupOfferConversation(
	message: Message.Message,
	contextId: Message.MessageContextKeys,
	offer: string,
	agr: string,
) {
	const i18n = getI18n();
	if (contextId !== 'agrToSupOffer') throw new Error(i18n.t('repository.contextAgrError'));
	if (!offer) throw new Error(i18n.t('repository.offerIdMissing'));
	const config: Message.AgrToSupOffer = {
		message: message,
		context: {
			agr: agr,
			offer: offer
		},
	};
	await post('oxs/message/createagrtosupofferconversation', config);
}

export async function sendConversation(
	message: Message.Message,
	contextId: Message.MessageContextKeys,
	offer?: string,
	sup?: string,
	agr?: string,
	country?: CountryUtil.CountryCode
) {
	const i18n = getI18n();
	if (contextId === 'autToSupOfferInCountry') {
		if (!offer) throw new Error(i18n.t('repository.offerIdMissing'));
		await autToSupOfferConversation(message, contextId, offer);
	} else if (contextId === 'supAcceptanceInCountry') {
		if (!sup || !country) throw new Error(i18n.t('repository.supIdMissing'));
		await autToSupReleaseConversation(message, contextId, sup, country);
	} else if (contextId === 'agrToSupOffer') {
		if (!agr) throw new Error(i18n.t('repository.agrMissing'));
		if (!offer) throw new Error(i18n.t('repository.offerAgrMissing'));
		await agrToSupOfferConversation(message, contextId, offer, agr);
	} else {
		throw new Error(i18n.t('repository.contextMissing'));
	}
}

export async function sendAnnouncement(
	announcement: string,
	attachments: Message.MessageAttachment[],
) {
	const config: Message.AutToSupAnnouncement = {
		message: {
			sent: new Date().toJSON(),
			content: announcement,
			party: 0,
			attachments: attachments,
		},
	};
	await post('oxs/message/createauttosupannouncement', config);
}

export async function sendMessageForMissingSpecies(
	message: string,
	species: string,
	category: string[],
) {
	const tokenStore = useTokenStore();
	const categoryLabels: string[] = [];
	for (const i of category) {
		const ent = await SearchRepo.getEntityById<SelectOption.SelectOption>(i);
		const label = toLocale(ent.data.labels);
		categoryLabels.push(label);
	}
	const categoryString = categoryLabels.join(', ');
	const config: Message.MissingSpecies = {
		context: {
			missingSpecies: tokenStore.accountId,
		},
		message: {
			sent: new Date().toJSON(),
			content: `${species}\n\n${message}\n\n${categoryString}`,
			attachments: undefined,
			party: 0,
		},
	};
	await post('oxs/message/createconversationformissingspecies', config);
}

export async function changePartyProps(config: Message.ChangeParties) {
	await post('oxs/message/changepartyforconversation', config);
}

export async function markMessageUnread(config: { conversation: string }) {
	await post('oxs/message/markmessageunread', config);
}

export async function createNewConversation(
	config: Message.MessageData,
): Promise<EAV.Entity<Message.MessageData>> {
	const sendToParty = config.parties.find((p) => !p.initiator)!;
	const newConvo: Message.CreateNewConversation = {
		party: sendToParty,
		message: config.messages[0],
	};
	return await post('oxs/message/createnewconversation', newConvo);
}

export async function notifyConOfLinking(
	accessForConsultants: UserTypes.UserDataAgr['accessForConsultants'],
	agr: string,
	language?: LanguageUtil.LocaleCode
) {
	for (const acc of accessForConsultants) {
		const config: { con: string, agr: string, language?: LanguageUtil.LocaleCode } = {
			con: acc.account,
			agr: agr,
			language: language
		}
		await post('oxs/message/notifyconoflinking', config);
	}
}

export async function notifySupForChangedStatus(
	supId: string,
	countryCode: CountryUtil.CountryCode,
	status: UserTypes.AutApprovalStatusLabels,
) {
	const config = {
		supId: supId,
		countryCode: countryCode,
		status: status,
	};
	await post('oxs/message/notifysupforchangedstatus', config);
}
